import { Injectable } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot
} from '@angular/router'
import { Observable, of } from 'rxjs'
import { delay, finalize } from 'rxjs/operators'
import { LocalStorageService } from '../../providers/local-storage/local-storage.service'
import { LoadingService } from '../../providers/loading/loading.service'

/**
 * This resolver redirects VENUE_ADMIN users immediately to their venue detail page
 * before the route component is even loaded. This provides instant redirect without delay.
 *
 * Usage in routes:
 * {
 *   path: '',
 *   component: PageComponent,
 *   resolve: { venueAdminRedirect: VenueAdminRedirectResolver },
 *   data: {
 *     venueAdminRedirectTo: '/orders/:hotelId', // or any other path template
 *     // ... other data
 *   },
 *   children: ...
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class VenueAdminRedirectResolver {
  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private loadingService?: LoadingService
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    const userProfile = this.localStorageService.get('userProfile')
    const redirectTemplate = route.data['venueAdminRedirectTo'] as
      | string
      | undefined

    // Check if user is HOTEL_ADMIN and has a hotelId, and redirect template is provided
    if (
      userProfile?.positionInSystem === 'HOTEL_ADMIN' &&
      userProfile?.hotelId &&
      redirectTemplate
    ) {
      // Show loading indicator during redirect process
      if (this.loadingService) {
        this.loadingService.showLoading()
      }

      // Replace the hotelId placeholder with the actual hotelId
      const redirectTo = redirectTemplate.replace(
        ':hotelId',
        userProfile.hotelId
      )

      // Add a small delay to show loading indicator and then redirect
      return of(null).pipe(
        delay(300), // Show loading for 300ms to provide visual feedback
        finalize(() => {
          // Hide loading and perform redirect
          if (this.loadingService) {
            this.loadingService.hideLoading()
          }
          this.router.navigateByUrl(redirectTo, { replaceUrl: true })
        })
      )
    }

    // Allow normal access for non-VENUE_ADMIN users or VENUE_ADMIN without hotelId
    return of(true)
  }
}
